import { HUBSPOT_CALLING_CONNECT_PATH, HUBSPOT_CALLING_PATH, OAUTH_CONSENT_PATH, PLATFORM_INVITATION_PATH } from '@lia/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams, type RouteObject } from 'react-router';
import { onAuthRequirement, onUnauthorized } from '../lib/api.js';
import { qk } from '../lib/query.js';
import { NotFoundPage, RouteErrorPage } from '../pages/not-found.js';
import { ModuleGate } from '../pages/modules/module-state.js';
import { HomeRedirect } from './home-redirect.js';
import { legacyRedirect } from './nav.js';
import { FullPageLoading, RequireSession } from './require-session.js';
import { PlatformLayout, WorkspaceLayout } from './workspace-layout.js';

// Route-level code splitting: each page module loads on first visit.
type PageModule = Record<string, unknown>;
const page = (load: () => Promise<PageModule>, name: string): RouteObject['lazy'] => async () => {
  const m = await load();
  return { Component: m[name] as React.ComponentType };
};

const conversations = page(() => import('../pages/conversations/conversations-page.js'), 'ConversationsPage');
const settings = page(() => import('../pages/settings/settings-page.js'), 'SettingsPage');
const copilot = (name: string) => page(() => import('../pages/modules/copilot-pages.js'), name);
const COPILOT_PAGES = {
  'sessions-page': () => import('../pages/copilot/sessions-page.js'),
  'session-page': () => import('../pages/copilot/session-page.js'),
  'compact-page': () => import('../pages/copilot/compact-page.js'),
  'playbooks-page': () => import('../pages/copilot/playbooks-page.js'),
  'playbook-page': () => import('../pages/copilot/playbook-page.js'),
  'settings-page': () => import('../pages/copilot/settings-page.js'),
  'reports-page': () => import('../pages/copilot/reports-page.js'),
} as const;
const copilotPage = (file: keyof typeof COPILOT_PAGES, name: string) => page(COPILOT_PAGES[file], name);
const onboarding = (name: string) => page(() => import('../pages/modules/module-onboarding-page.js'), name);

/** Old module paths (conversaciones, llamadas, agentes, automatizaciones, campanas) → their module, keeping ?query and #hash. */
function LegacyRedirect() {
  const { workspaceId = '' } = useParams();
  const { pathname, search, hash } = useLocation();
  const rest = pathname.replace(/^\/w\/[^/]+\/?/, '').replace(/\/+$/, '');
  const to = legacyRedirect(rest);
  return to ? <Navigate to={`/w/${workspaceId}/${to}${search}${hash}`} replace /> : <NotFoundPage />;
}

export const appRoutes: RouteObject[] = [
  // Public auth pages
  { path: '/ingresar', lazy: page(() => import('../pages/auth/sign-in.js'), 'SignInPage'), errorElement: <RouteErrorPage /> },
  { path: '/olvide-contrasena', lazy: page(() => import('../pages/auth/forgot-password.js'), 'ForgotPasswordPage'), errorElement: <RouteErrorPage /> },
  { path: '/restablecer', lazy: page(() => import('../pages/auth/reset-password.js'), 'ResetPasswordPage'), errorElement: <RouteErrorPage /> },
  { path: '/verificar-correo', lazy: page(() => import('../pages/auth/verify-email.js'), 'VerifyEmailPage'), errorElement: <RouteErrorPage /> },
  { path: '/invitacion', lazy: page(() => import('../pages/auth/invitation.js'), 'InvitationPage'), errorElement: <RouteErrorPage /> },
  // «Equipo Estateflow»: invitation to platform capabilities (signs in or creates the account with the invited email).
  { path: PLATFORM_INVITATION_PATH, lazy: page(() => import('../pages/auth/platform-invitation.js'), 'PlatformInvitationPage'), errorElement: <RouteErrorPage /> },
  // HubSpot calling extension (plan B6): the only page HubSpot may frame. No session gate: inside HubSpot's iframe
  // the session cookie is not sent; the page signs in with its own popup flow (embed token in memory).
  { path: HUBSPOT_CALLING_PATH, lazy: page(() => import('../pages/hubspot-calling/hubspot-calling-page.js'), 'HubspotCallingPage'), errorElement: <RouteErrorPage /> },
  // Session required, MFA may still be pending
  {
    element: <RequireSession mfaStep />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/verificacion', lazy: page(() => import('../pages/auth/mfa-verify.js'), 'MfaVerifyPage') },
      { path: '/seguridad', lazy: page(() => import('../pages/auth/security-enrollment.js'), 'SecurityEnrollmentPage') },
    ],
  },
  {
    element: <RequireSession />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/', element: <HomeRedirect /> },
      { path: '/espacios/nuevo', lazy: page(() => import('../pages/auth/create-workspace.js'), 'CreateWorkspacePage') },
      // First-party sign-in popup of the HubSpot calling extension (top level, normal session; never framed).
      { path: HUBSPOT_CALLING_CONNECT_PATH, lazy: page(() => import('../pages/hubspot-calling/connect-page.js'), 'HubspotCallingConnectPage') },
      // OAuth consent of the remote MCP server (ChatGPT connector, docs/mcp.md): top level, normal session + MFA.
      { path: OAUTH_CONSENT_PATH, lazy: page(() => import('../pages/oauth/consent-page.js'), 'OauthConsentPage') },
      {
        element: <PlatformLayout />,
        children: [
          { path: '/plataforma', element: <Navigate to="/plataforma/instrucciones" replace /> },
          { path: '/plataforma/instrucciones', lazy: page(() => import('../pages/platform/platform-page.js'), 'PlatformPage') },
          // Short link to «Plataforma → Equipo Estateflow» (docs/mcp.md); the tab itself checks the permission.
          { path: '/plataforma/equipo', element: <Navigate to="/plataforma/instrucciones?seccion=equipo" replace /> },
        ],
      },
      // Compact companion window of a copilot session: same session and permissions, no app chrome (opened with
      // window.open from the live screen; it reloads from server state and never relies on the opener tab).
      {
        path: '/w/:workspaceId/copilot/compact/:sessionId',
        element: <WorkspaceLayout bare />,
        children: [{ element: <ModuleGate module="copilot" />, children: [{ index: true, lazy: copilotPage('compact-page', 'CopilotCompactPage') }] }],
      },
      {
        path: '/w/:workspaceId',
        element: <WorkspaceLayout />,
        children: [
          { index: true, element: <Navigate to="inicio" replace /> },
          { path: 'inicio', lazy: page(() => import('../pages/home/home-page.js'), 'HomePage') },
          { path: 'configuracion/inicio', lazy: page(() => import('../pages/home/setup-page.js'), 'SetupPage') },
          { path: 'configuracion/modulos', lazy: page(() => import('../pages/home/module-selection-page.js'), 'ModuleSelectionPage') },
          { path: 'prospectos', lazy: page(() => import('../pages/leads/leads-page.js'), 'LeadsPage') },
          { path: 'prospectos/importar', lazy: page(() => import('../pages/campaigns/import-page.js'), 'ImportPage') },
          { path: 'citas', lazy: page(() => import('../pages/appointments/appointments-page.js'), 'AppointmentsPage') },
          { path: 'incidencias', lazy: page(() => import('../pages/incidents/incidents-page.js'), 'IncidentsPage') },
          { path: 'proyectos', lazy: page(() => import('../pages/projects/projects-page.js'), 'ProjectsPage') },
          { path: 'proyectos/:projectId', lazy: page(() => import('../pages/projects/project-detail-page.js'), 'ProjectDetailPage') },
          { path: 'proyectos/:projectId/:tab', lazy: page(() => import('../pages/projects/project-detail-page.js'), 'ProjectDetailPage') },
          { path: 'conocimiento', lazy: page(() => import('../pages/knowledge/knowledge-page.js'), 'KnowledgePage') },
          // Agente WhatsApp
          {
            path: 'whatsapp',
            element: <ModuleGate module="whatsapp" />,
            children: [
              { index: true, lazy: page(() => import('../pages/modules/module-overview-page.js'), 'WhatsAppOverviewPage') },
              { path: 'inbox', lazy: conversations },
              { path: 'inbox/:conversationId', lazy: conversations },
              { path: 'automations', lazy: page(() => import('../pages/automations/automations-page.js'), 'AutomationsPage') },
              { path: 'automations/:routineId', lazy: page(() => import('../pages/automations/routine-page.js'), 'RoutinePage') },
              { path: 'templates', lazy: page(() => import('../pages/whatsapp-templates/templates-page.js'), 'WhatsAppTemplatesPage') },
              { path: 'campaigns', lazy: page(() => import('../pages/campaigns/campaigns-page.js'), 'CampaignsPage') },
              { path: 'campaigns/:campaignId', lazy: page(() => import('../pages/campaigns/campaign-page.js'), 'CampaignPage') },
              { path: 'settings', lazy: page(() => import('../pages/agents/agents-page.js'), 'WhatsAppSettingsPage') },
              { path: 'test', lazy: page(() => import('../pages/agents/agent-simulator-page.js'), 'WhatsAppTestPage') },
              { path: 'reports', lazy: page(() => import('../pages/reports/reports-page.js'), 'WhatsAppReportsPage') },
              { path: 'onboarding', lazy: onboarding('WhatsAppOnboardingPage') },
            ],
          },
          // Agente de llamadas IA
          {
            path: 'ai-calls',
            element: <ModuleGate module="ai_calls" />,
            children: [
              { index: true, lazy: page(() => import('../pages/modules/module-overview-page.js'), 'AiCallsOverviewPage') },
              { path: 'calls', lazy: page(() => import('../pages/calls/calls-page.js'), 'CallsPage') },
              { path: 'queue', lazy: page(() => import('../pages/priorities/call-queue-page.js'), 'CallQueuePage') },
              { path: 'settings', lazy: page(() => import('../pages/modules/ai-calls-settings-page.js'), 'AiCallsSettingsPage') },
              { path: 'test', lazy: page(() => import('../pages/agents/agent-simulator-page.js'), 'AiCallsTestPage') },
              { path: 'reports', lazy: page(() => import('../pages/reports/reports-page.js'), 'AiCallsReportsPage') },
              { path: 'onboarding', lazy: onboarding('AiCallsOnboardingPage') },
            ],
          },
          // Copiloto de llamadas (live transcription from providers is still «sin verificar»)
          {
            path: 'copilot',
            element: <ModuleGate module="copilot" />,
            children: [
              { index: true, lazy: copilot('CopilotOverviewPage') },
              { path: 'sessions', lazy: copilotPage('sessions-page', 'CopilotSessionsPage') },
              { path: 'sessions/:sessionId', lazy: copilotPage('session-page', 'CopilotSessionPage') },
              { path: 'playbooks', lazy: copilotPage('playbooks-page', 'CopilotPlaybooksPage') },
              { path: 'playbooks/:playbookId', lazy: copilotPage('playbook-page', 'CopilotPlaybookPage') },
              { path: 'settings', lazy: copilotPage('settings-page', 'CopilotSettingsPage') },
              { path: 'reports', lazy: copilotPage('reports-page', 'CopilotReportsPage') },
              { path: 'onboarding', lazy: onboarding('CopilotOnboardingPage') },
            ],
          },
          // Old paths before the module navigation
          { path: 'conversaciones/*', element: <LegacyRedirect /> },
          { path: 'llamadas/*', element: <LegacyRedirect /> },
          { path: 'agentes/*', element: <LegacyRedirect /> },
          { path: 'automatizaciones/*', element: <LegacyRedirect /> },
          { path: 'campanas/*', element: <LegacyRedirect /> },
          { path: 'integraciones', lazy: page(() => import('../pages/integrations/integrations-page.js'), 'IntegrationsPage') },
          { path: 'contingencia', lazy: page(() => import('../pages/contingency/contingency-page.js'), 'ContingencyPage') },
          { path: 'revision-abuso', lazy: page(() => import('../pages/abuse/abuse-review-page.js'), 'AbuseReviewPage') },
          { path: 'equipo', lazy: page(() => import('../pages/team/team-page.js'), 'TeamPage') },
          { path: 'mi-trabajo', lazy: page(() => import('../pages/advisors/my-work-page.js'), 'MyWorkPage') },
          { path: 'mis-llamadas', lazy: page(() => import('../pages/advisor-calls/advisor-calls-page.js'), 'AdvisorCallsPage') },
          { path: 'asesores', lazy: page(() => import('../pages/advisors/advisors-page.js'), 'AdvisorsPage') },
          { path: 'asesores/auditorias/:auditId', lazy: page(() => import('../pages/advisors/audit-detail-page.js'), 'AuditDetailPage') },
          { path: 'reportes', lazy: page(() => import('../pages/reports/reports-page.js'), 'ReportsPage') },
          { path: 'configuracion', element: <Navigate to="espacio" replace /> },
          { path: 'configuracion/:tab', lazy: settings },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
];

export function createAppRouter(queryClient: QueryClient) {
  // Root route supplies the fallback shown while the first lazy page module loads.
  const router = createBrowserRouter([{ element: <Outlet />, hydrateFallbackElement: <FullPageLoading label="Cargando" />, children: appRoutes }]);
  // The HubSpot calling page handles its own 401s (embed token): never redirect HubSpot's iframe to sign-in.
  const publicPaths = ['/ingresar', '/olvide-contrasena', '/restablecer', '/verificar-correo', '/invitacion', PLATFORM_INVITATION_PATH, HUBSPOT_CALLING_PATH];

  // Any 401 from the API: drop cached data and go to sign-in (keeping where the user was).
  let redirecting = false;
  onUnauthorized(() => {
    const path = router.state.location.pathname;
    if (redirecting || publicPaths.includes(path)) return;
    redirecting = true;
    const next = path + router.state.location.search;
    const hadSession = queryClient.getQueryData(qk.me) !== undefined;
    queryClient.clear();
    void router
      .navigate(`/ingresar?siguiente=${encodeURIComponent(next)}${hadSession ? '&expirada=1' : ''}`, { replace: true })
      .finally(() => {
        redirecting = false;
      });
  });

  // 403 mfa_required / mfa_enrollment_required: send the user to the matching step.
  onAuthRequirement((req) => {
    const { pathname, search } = router.state.location;
    const target = req === 'mfa_required' ? '/verificacion' : '/seguridad';
    if (pathname === target) return;
    void queryClient.invalidateQueries({ queryKey: qk.me });
    void router.navigate(`${target}?siguiente=${encodeURIComponent(pathname + search)}`, { replace: true });
  });
  return router;
}
